import { cn } from '@/utils/cn';

type TricoloreVariant =
  | 'divider'
  | 'thin'
  | 'compact'
  | 'gradient'
  | 'navrule';

type TricoloreProps = {
  variant?: TricoloreVariant;
  className?: string;
};

/**
 * Italienisches Tricolore-Element — Grün → Gold → Rot.
 * Brand-Element der Sizilianer (Gold ersetzt Weiß).
 *
 * - `divider`  vollbreite 3-Felder-Stripes (~3px)
 * - `thin`     1.5px Variante (z.B. Admin-Page-Top-Border)
 * - `compact`  schmaler 60px Akzent (z.B. unter Headlines)
 * - `gradient` weiche Übergänge zur Mitte (z.B. Section-Footer)
 * - `navrule`  Tricolore-Linie unter dem Header mit Gold-Diamant + 2 Punkten
 */
export function Tricolore({ variant = 'divider', className }: TricoloreProps) {
  if (variant === 'navrule') {
    return (
      <div
        className={cn(
          'relative grid items-center gap-4',
          'grid-cols-[1fr_auto_1fr]',
          className,
        )}
        aria-hidden="true"
      >
        <span
          className="block h-[1.5px]"
          style={{
            background:
              'linear-gradient(to right, var(--color-it-green), var(--color-rule))',
          }}
        />
        <span className="relative flex items-center justify-center">
          <span className="size-1.5 bg-gold -translate-x-3.5 absolute" />
          <span className="size-2.5 bg-gold rotate-45" />
          <span className="size-1.5 bg-gold translate-x-3.5 absolute" />
        </span>
        <span
          className="block h-[1.5px]"
          style={{
            background:
              'linear-gradient(to right, var(--color-rule), var(--color-it-red))',
          }}
        />
      </div>
    );
  }

  if (variant === 'gradient') {
    return (
      <div
        className={cn('flex items-center justify-center gap-4', className)}
        aria-hidden="true"
      >
        <span className="block h-px w-32 bg-gradient-to-r from-transparent to-it-green" />
        <span className="block h-1.5 w-1.5 rotate-45 border border-gold" />
        <span className="block h-px w-32 bg-gradient-to-l from-transparent to-it-red" />
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={cn('flex h-px w-16', className)} aria-hidden="true">
        <span className="flex-1 bg-it-green" />
        <span className="flex-1 bg-gold" />
        <span className="flex-1 bg-it-red" />
      </div>
    );
  }

  const heightClass = variant === 'thin' ? 'h-px' : 'h-[3px]';

  return (
    <div
      className={cn('flex w-full', heightClass, className)}
      role="presentation"
      aria-hidden="true"
    >
      <span className="flex-1 bg-it-green" />
      <span className="flex-1 bg-gold" />
      <span className="flex-1 bg-it-red" />
    </div>
  );
}
